import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isEmpty } from '@ember/utils'
import {
  reportingLegend as legend,
  reportingPlotLine as plotLine,
} from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type {
  AxisLabelsFormatterContextObject,
  LegendOptions,
  SeriesLineOptions,
  Point,
  YAxisPlotLinesOptions,
} from 'highcharts'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SeriesLine } from 'teacher-ui-client/components/giraffe/primitives/series-line'
import { isPresent } from '@ember/utils'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'

export interface ReadingProgressAveragesData {
  grade_position: number
  months: {
    month: string
    avg_max_lesson: Nullable<number>
  }[]
}

interface Args {
  data: ReadingProgressAveragesData
  precinct:
    | typeof Precinct.RE_READING
    | typeof Precinct.RE_SPELLING
    | typeof Precinct.REX_READING
    | typeof Precinct.REX_SPELLING
    | typeof Precinct.MS_LESSONS
  title: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class CourseProgressAverages extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  @service lessonInfo!: LessonInfoService

  /**
   * Chart Margin.
   * Override the right margin to allow space for plot line labels.
   */
  readonly chartMargin: (number | null)[] = [null, 120, null, null]

  /**
   * Chart legend.
   */
  readonly legend: LegendOptions = legend('left', 'top', 50, 10)

  /**
   * List of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories(): string[] {
    const monthsData = this.args.data.months
    return monthsData.map((monthData) => convertDateFormat(monthData.month))
  }

  /**
   * List of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get chartData(): SeriesLineOptions[] {
    const monthsData = this.args.data.months

    const data = monthsData.map((monthData, index) => {
      return { x: index, y: monthData.avg_max_lesson, monthData }
    })

    return [{ name: 'avg_max_lesson', color: this.color, data, type: 'line' }]
  }

  /**
   * Theme colours for the chart
   */
  get color() {
    switch (this.args.precinct) {
      case Precinct.MS_LESSONS:
        return colors.forestGreen300
      case Precinct.RE_SPELLING:
      case Precinct.REX_SPELLING:
        return colors.grapeyGreen300
      case Precinct.RE_READING:
      case Precinct.REX_READING:
        return colors.oceanyBlue300
      default:
        return colors.oceanyBlue300
    }
  }

  /**
   * The title of the chart
   */
  get hyphenatedTitle(): string {
    const mainChartTitle = this.args.title
    const gradePosition = this.args.data.grade_position
    if (typeof gradePosition !== 'number') return `${mainChartTitle}`
    const gradeSuffix = this.gradeSets.findByPosition(gradePosition)?.fullName
    return `${mainChartTitle} - ${gradeSuffix}`
  }

  get lessons() {
    return this.lessonInfo.lessonsFor(this.args.precinct)
  }

  /**
   * Provide the grades for the lessons in the reporting data
   */
  get grades() {
    const monthsData = this.args.data.months
    const maxLessons = monthsData.map((month) => month.avg_max_lesson).filter(isPresent)
    if (isEmpty(maxLessons)) return []

    const grades = maxLessons
      .map((lesson) => this.lessons.find((l) => l.lesson_position === lesson)?.grade_position)
      .filter(isPresent)
    return [...new Set(grades)].sort((a, b) => a - b)
  }

  /**
   * Plot lines to mark the grade bands for the lesson values.
   */
  get plotLines(): YAxisPlotLinesOptions[] {
    // Add the _next_ grade to the list, if it exists in the lesson data. This is so we show the next plotLine too.
    const nextGrade = this.grades[this.grades.length - 1] + 1
    const gradesWithNext = this.lessons.find((l) => l.grade_position === nextGrade)
      ? [...this.grades, nextGrade]
      : this.grades

    return gradesWithNext
      .map((grade) => {
        const firstLessonOfGrade = this.lessons.find((l) => l.grade_position === grade)?.lesson_position
        const label = this.gradeSets.findByPosition(grade)?.fullName ?? ''

        if (typeof firstLessonOfGrade === 'number') return plotLine(firstLessonOfGrade, label)
        return null
      })
      .filter(isPresent)
  }

  /**
   * Returns a formatter function to pass to Highcharts to format the tooltip label.
   */
  get toolTipFormatter() {
    const { formatLessonPosition } = this
    const { precinct } = this.args

    return function (this: Point) {
      if (typeof this.y !== 'number') return null
      return `<strong>${formatLessonPosition(this.y, precinct)}</strong>`
    }
  }

  /**
   * Returns a formatter function to pass to Highcharts to format the y axis label.
   */
  get yAxisLabelFormatter() {
    const { formatLessonPosition } = this
    const { precinct } = this.args

    return function (this: AxisLabelsFormatterContextObject): string {
      if (typeof this.value !== 'number') return ''
      return formatLessonPosition(this.value, precinct)
    }
  }

  /**
   * Set the yRange of the chart to include all lessons for all grades in the data
   */
  get yRange(): [number, number] {
    const firstGrade = Math.min(...this.grades)
    const lastGrade = Math.max(...this.grades)

    /* expand grades to lessons range for the chart
     * min: first lesson for first grade
     * max: last lesson for last grade
     */
    const firstLesson = this.lessons.find((l) => l.grade_position === firstGrade)
    const lastLesson = [...this.lessons].reverse().find((l) => l.grade_position === lastGrade)

    // 1 is added to the last lesson, so that the nextGrade plotLine is part of the range
    return [firstLesson?.lesson_position ?? -Infinity, (lastLesson?.lesson_position ?? Infinity) + 1]
  }

  /**
   * Format the value based on the current precinct.
   */
  formatLessonPosition = (lessonPos: number, precinct: string): string => {
    if (precinct === Precinct.REX_SPELLING) {
      const lessons = this.lessonInfo.lessonsFor(precinct)
      const lesson = lessons.find((l) => l.lesson_position === lessonPos)
      if (!lesson || !lesson.grade_position || !lesson.grade_lesson_number) return lessonPos.toString()

      // 36 lessons in a grade, returns something like 2.36, or 2.05
      return `${lesson.grade_position}.${lesson.grade_lesson_number.toString().padStart(2, '0')}`
    }
    return lessonPos.toString()
  }

  <template>
    <div data-test-giraffe-chart-course-progress-averages class="relative h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <SeriesLine
        @title={{this.hyphenatedTitle}}
        @data={{this.chartData}}
        @categories={{this.categories}}
        @yAxisLabel="Lesson number"
        @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
        @toolTipFormatter={{this.toolTipFormatter}}
        @plotLines={{this.plotLines}}
        @chartMargin={{this.chartMargin}}
        @yRange={{this.yRange}}
      />
    </div>
  </template>
}

export default CourseProgressAverages
